import { EntityAdapter, EntityState, createEntityAdapter } from '@ngrx/entity';

import {
  ProformaActionTypes,
  ProformaActionsUnion
} from '@core/actions/proforma.actions';

import {
  ReportsActionTypes,
  ReportsActionsUnion
} from '@core/actions/reports.actions';

import { Proforma } from '@app/models/proforma';
import { createFeatureSelector, createSelector } from '@ngrx/store';

export interface State extends EntityState<Proforma> {
  loading: boolean;
  loaded: boolean;
  busy: boolean;
  waitingForProductsWithoutSku: boolean;
  dataProformsWithoutSku: any;
  message: string;
  ProformaIds: any[];
  ProductIds: any[];
  summary: any;
  enrichProductsInfo: any[];
  departmentSheets: any[];
  n2Departments: any[];
  supplierFileInfo: any[];
}

export const adapter: EntityAdapter<Proforma> = createEntityAdapter<Proforma>({
  selectId: proforma => proforma.id
});

export const INITIAL_STATE: State = adapter.getInitialState({
  loading: false,
  loaded: false,
  busy: false,
  message: null,
  waitingForProductsWithoutSku: false,
  dataProformsWithoutSku: {},
  ProformaIds: [],
  ProductIds: [],
  summary: {},
  enrichProductsInfo: [],
  departmentSheets: [],
  n2Departments: [],
  supplierFileInfo: []
});

export function reducer(
  state: State = INITIAL_STATE,
  action: ProformaActionsUnion | ReportsActionsUnion
) {
  switch (action.type) {
    case ProformaActionTypes.Load:
    case ProformaActionTypes.DiscrepanciesWebLoad:
    case ProformaActionTypes.RecalculateWeightedFob:
    case ProformaActionTypes.AllEnrichProducts:
    case ProformaActionTypes.DownloadEnrichProducts:
    case ProformaActionTypes.GetUploadLink:
    case ProformaActionTypes.UpsertEnrichProducts:
    case ProformaActionTypes.AllDepartmentSheets:
    case ProformaActionTypes.N2Department:
    case ProformaActionTypes.MigrateProductsShipments:
    case ProformaActionTypes.NewProformaProduct:
    case ProformaActionTypes.UpdateOne:
    case ProformaActionTypes.SupplierFile:
    case ProformaActionTypes.SupplierFileUpdate:
      return { ...state, busy: true, loading: true };

    case ProformaActionTypes.DownloadEnrichProductsSuccess:
    case ProformaActionTypes.DownloadEnrichProductsFail:
    case ProformaActionTypes.UploadFileSuccess:
    case ProformaActionTypes.UploadFileFail:
    case ProformaActionTypes.UpsertEnrichProductsFail:
    case ProformaActionTypes.UpsertEnrichProductsSuccess:
    case ProformaActionTypes.MigrateProductsShipmentsSuccess:
    case ProformaActionTypes.MigrateProductsShipmentsFail:
    case ProformaActionTypes.NewProformaProductSuccess:
    case ProformaActionTypes.NewProformaProductFail:
    case ProformaActionTypes.UpdateOneFail:
    case ProformaActionTypes.SupplierFileFail:
    case ProformaActionTypes.DiscrepanciesWebLoadFail:
      return { ...state, busy: false, loading: false };

    case ProformaActionTypes.LoadFail:
      return { ...state, loading: false, busy: false };

    case ProformaActionTypes.SupplierFileSuccess:
      return {
        ...state,
        supplierFileInfo: action.data,
        busy: false,
        loading: false
      };

    case ProformaActionTypes.DiscrepanciesWebLoadSuccess:
      return adapter.addAll(action.data, {
        ...state,
        busy: false,
        loading: false,
        loaded: true
      });
    case ProformaActionTypes.LoadSuccess:
    case ProformaActionTypes.GenerateSuccess:
      return adapter.addAll(action.proformas, {
        ...state,
        busy: false,
        loading: false,
        loaded: true
      });

    case ProformaActionTypes.CreateSuccess:
      return adapter.addOne(action.proforma, {
        ...state
      });

    case ProformaActionTypes.ExportGroup:
      return {
        ...state,
        busy: true,
        message: 'Exportando grupo de proformas'
      };

    case ReportsActionTypes.GetUploadLink:
      return {
        ...state,
        busy: true,
        c: 'Procesando proformas...'
      };

    case ReportsActionTypes.GetUploadLinkNew:
      state.waitingForProductsWithoutSku = false;
      return {
        ...state,
        busy: true,
        c: 'Procesando proformas...'
      };

    case ReportsActionTypes.UploadProcessSuccess:
      return {
        ...state,
        busy: false,
        spinnerMessage: ''
      };

    case ProformaActionTypes.Generate:
      return {
        ...state,
        busy: true,
        message: 'Generando proformas...'
      };

    case ProformaActionTypes.ProcessFileWithSkusSuccess:
      state.busy = false;
      return {
        ...state
      };

    case ProformaActionTypes.UpdateGenerate:
      return {
        ...state,
        busy: true,
        message: 'Actualizando proformas...'
      };

    case ProformaActionTypes.UpdateGenerateSuccess:
      return {
        ...state,
        busy: false,
        message: ''
      };

    case ProformaActionTypes.UpdateGenerateFail:
      return {
        ...state,
        busy: false,
        loading: false,
        message: ''
      };

    case ProformaActionTypes.ExportGroupUpdate:
      return {
        ...state,
        busy: true,
        message: action.data.message
      };

    case ProformaActionTypes.EnrichProducts:
      return {
        ...state,
        busy: true,
        message: 'Procesando productos...'
      };

    case ProformaActionTypes.AllEnrichProductsSuccess:
      return {
        ...state,
        busy: false,
        loading: false,
        enrichProductsInfo: action.data
      };

    case ProformaActionTypes.N2DepartmentSuccess:
      return {
        ...state,
        busy: false,
        loading: false,
        n2Departments: action.data
      };

    case ProformaActionTypes.AllDepartmentSheetsSuccess:
      return {
        ...state,
        busy: false,
        loading: false,
        departmentSheets: action.data
      };

    case ProformaActionTypes.ExportGroupFail:
    case ProformaActionTypes.ExportGroupDownloadSuccess:
    case ProformaActionTypes.ExportGroupDownloadFail:
    case ProformaActionTypes.EnrichProductsSuccess:
    case ProformaActionTypes.EnrichProductsFail:
    case ProformaActionTypes.AllEnrichProductsFail:
    case ProformaActionTypes.AllDepartmentSheetsFail:
    case ProformaActionTypes.N2DepartmentFail:
      return {
        ...state,
        busy: false,
        message: null
      };

    case ProformaActionTypes.UpdateOneSuccess:
      if (!action.data || !action.data.id) {
        return { ...state, busy: false, loading: false };
      }
      // merge parcial para no perder asociaciones que la respuesta
      // del API no incluye (Provider, Shipment, etc.)
      return adapter.updateOne(
        { id: action.data.id, changes: action.data },
        {
          ...state,
          busy: false,
          loading: false
        }
      );

    case ProformaActionTypes.UpdateCancelSuccess:
    case ProformaActionTypes.UpdateSuccess:
      if (!action.proforma || !action.proforma.id) {
        return { ...state, busy: false, loading: false };
      }
      // merge parcial para conservar las asociaciones ya cargadas
      return adapter.updateOne(
        { id: action.proforma.id, changes: action.proforma },
        { ...state, busy: false, loading: false }
      );

    case ProformaActionTypes.UpdateStatusSuccess:
      return adapter.upsertMany(action.data, state);

    case ProformaActionTypes.Search:
      return {
        ...state,
        ProformaIds: [],
        loading: true
      };

    case ProformaActionTypes.SearchSuccess:
      return {
        ...state,
        ProformaIds:
          action.ProformaIds.length === 0 ? [-1] : action.ProformaIds,
        loading: false
      };

    case ProformaActionTypes.Summary:
      return {
        ...state,
        summary: [],
        loading: true
      };

    case ProformaActionTypes.SummarySuccess:
      return {
        ...state,
        summary: action.savedSummary,
        loading: false,
        busy: false,
        message: ''
      };

    case ProformaActionTypes.SearchFail:
      return {
        ...state,
        ProformaIds: [],
        loading: false
      };

    case ProformaActionTypes.ClearSearch:
      return {
        ...state,
        ProformaIds: [],
        loading: false
      };

    case ProformaActionTypes.SearchProduct:
      return {
        ...state,
        ProductIds: [],
        loading: true
      };

    case ProformaActionTypes.SearchProductSuccess:
      // console.log('asdasdasd', action.ProductIds, action.ProductIds.length);
      return {
        ...state,
        ProductIds: action.ProductIds.length === 0 ? [-1] : action.ProductIds,
        loading: false
      };

    case ProformaActionTypes.SearchProductFail:
      return {
        ...state,
        ProductIds: [],
        loading: false
      };

    case ProformaActionTypes.SearchClearProduct:
      return {
        ...state,
        ProductIds: [],
        loading: false
      };

    case ProformaActionTypes.ProcessFileSuccess:
      state.busy = false;
      return {
        ...state,
        waitingForProductsWithoutSku: true,
        dataProformsWithoutSku: action.data.message
      };

    case ProformaActionTypes.ProcessFileCancel:
      return {
        ...state,
        waitingForProductsWithoutSku: false
      };

    case ProformaActionTypes.ClearState:
      return INITIAL_STATE;

    default:
      return state;
  }
}

export const {
  selectIds,
  selectEntities,
  selectAll,
  selectTotal
} = adapter.getSelectors();

export const selectLoaded = (state: State) => state.loaded;
export const selectLoading = (state: State) => state.loading;

export const getState = createFeatureSelector<State>('proformas');
export const selectProformaIds = (state: State) => state.ProformaIds;
export const selectProductIds = (state: State) => state.ProductIds;

export const getWaiting = createSelector(
  getState,
  state => state.waitingForProductsWithoutSku
);

export const getAll = createSelector(
  getState,
  selectAll
);

export const getSummary = createSelector(
  getState,
  (state: State) => state.summary
);

export const getLoaded = createSelector(
  getState,
  selectLoaded
);

export const getMessage = createSelector(
  getState,
  (state: State) => state.message
);

export const getBusy = createSelector(
  getState,
  (state: State) => state.busy
);

export const getIsLoading = createSelector(
  getState,
  (state: State) => state.loading
);

export const getDataProformsWithoutSku = createSelector(
  getState,
  (state: State) => state.dataProformsWithoutSku
);

export const getProformasEntities = createSelector(
  getAll,
  proformas => {
    return (
      proformas &&
      proformas.length &&
      proformas.reduce((acc, proforma) => {
        acc[proforma.id] = proforma;
        return acc;
      }, {})
    );
  }
);

export const getFilteredProformaIds = createSelector(
  getState,
  selectProformaIds
);

export const getProductIdsDisabledByFob = createSelector(
  getState,
  selectProductIds
);

export const getEnrichProductsInfo = createSelector(
  getState,
  (state: State) => state.enrichProductsInfo
);

export const getDepartmentSheetsInfo = createSelector(
  getState,
  (state: State) => state.departmentSheets
);

export const getn2Departments = createSelector(
  getState,
  (state: State) => state.n2Departments
);

export const getSupplierFileinfo = createSelector(
  getState,
  (state: State) => state.supplierFileInfo
);
